import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '../services/ApiService';
import { snapshotService } from '../services/SnapshotService';

// Default settings for fallback - always use admin content
const defaultPublicSettings = {
    aboutContent: undefined,
    etsyButtonImageId: undefined,
    instagramButtonImageId: undefined,
};

// Singleton pattern to prevent duplicate API calls across multiple provider instances
let globalSettingsPromise: Promise<{ settings: { key: string; value: string }[] }> | null = null;
let globalSettingsCache: { settings: { key: string; value: string }[] } | null = null;

interface PublicSettingsData {
    aboutContent?: string;
    etsyButtonImageId?: string;
    instagramButtonImageId?: string;
    etsyButtonImageUrl?: string;
    instagramButtonImageUrl?: string;
    logoOverrideUrl?: string;
}

interface PublicSettingsContextType {
    settings: PublicSettingsData;
    loading: boolean;
    error: string | null;
    refreshSettings: () => Promise<void>;
}

export const PublicSettingsContext = createContext<PublicSettingsContextType | undefined>(undefined);

interface PublicSettingsProviderProps {
    children: ReactNode;
}

export const PublicSettingsProvider: React.FC<PublicSettingsProviderProps> = ({ children }) => {
    const [settings, setSettings] = useState<PublicSettingsData>(defaultPublicSettings);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const loadSettings = useCallback(async () => {
        // Prevent duplicate calls - use a more robust check
        if (hasLoaded || loading || isInitialized) {
            return;
        }
        
        setIsInitialized(true);
        setLoading(true);
        setError(null);
        
        try {
            let response: { settings: { key: string; value: string }[] };
            
            // Use singleton pattern to prevent duplicate API calls
            if (globalSettingsCache) {
                response = globalSettingsCache;
            } else if (globalSettingsPromise) {
                response = await globalSettingsPromise;
            } else {
                // Try snapshot service first, fallback to API
                globalSettingsPromise = snapshotService.loadSettingsData(() => apiService.getPublicSettings());
                response = await globalSettingsPromise;
                globalSettingsCache = response;
                globalSettingsPromise = null; // Clear the promise after completion
            }
            
            // Convert API settings to our local format
            const apiSettings: PublicSettingsData = { ...defaultPublicSettings };
            
            response.settings.forEach((setting) => {
                switch (setting.key) {
                    case 'about_content':
                        apiSettings.aboutContent = setting.value || undefined;
                        break;
                    case 'etsy_button_image_id':
                        apiSettings.etsyButtonImageId = setting.value || undefined;
                        break;
                    case 'instagram_button_image_id':
                        apiSettings.instagramButtonImageId = setting.value || undefined;
                        break;
                    // Handle resolved URLs from snapshots
                    case 'etsy_button_image_url':
                        apiSettings.etsyButtonImageUrl = setting.value || undefined;
                        break;
                    case 'instagram_button_image_url':
                        apiSettings.instagramButtonImageUrl = setting.value || undefined;
                        break;
                    case 'logo_override_url':
                        apiSettings.logoOverrideUrl = setting.value || undefined;
                        break;
                    default:
                        // Ignore other settings
                        break;
                }
            });
            
            setSettings(apiSettings);
            setHasLoaded(true);
        } catch (err) {
            console.error('Error loading public settings:', err);
            setError('Failed to load settings');
            // Fall back to default settings (admin content)
            setSettings(defaultPublicSettings);
            setHasLoaded(true);
            // Clear the failed promise so retry is possible
            globalSettingsPromise = null;
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array - this function should never change

    const refreshSettings = async () => {
        // Clear global cache to force fresh API call
        globalSettingsCache = null;
        globalSettingsPromise = null;
        
        setHasLoaded(false);
        setIsInitialized(false);
        await loadSettings();
    };

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const value: PublicSettingsContextType = {
        settings,
        loading,
        error,
        refreshSettings,
    };

    return (
        <PublicSettingsContext.Provider value={value}>
            {children}
        </PublicSettingsContext.Provider>
    );
};

export const usePublicSettings = (): PublicSettingsContextType => {
    const context = useContext(PublicSettingsContext);
    if (context === undefined) {
        throw new Error('usePublicSettings must be used within a PublicSettingsProvider');
    }
    return context;
};
